import React, {PureComponent} from "react";
import withStyles from '@mui/styles/withStyles';
import {UrlBuilder} from "../../../core/url/UrlBuilder";
import Grid from "@mui/material/Grid";
import WorkoutSmallNote from "../partials/WorkoutSmallNote";
import Box from "@mui/material/Box";
import moment from "moment";
import {Typography} from "@mui/material";
import Divider from "@mui/material/Divider";
import Skeleton from '@mui/material/Skeleton';
import {SentimentVeryDissatisfied, SentimentVerySatisfied} from "@mui/icons-material";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import IconButton from "@mui/material/IconButton";
import {withRouter} from "../../../hooks/withRouter";
import WorkoutLogsSection from "../partials/WorkoutLogsSection";
import PersonalRecordBanner from "../partials/PersonalRecordBanner";

const useStyles = theme => ({
    media: {
        height: 125,
    },
    sharedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: 16,
        padding: '2px 12px',
        fontSize: '0.75rem',
        fontWeight: 600,
    },
});

class SharedWorkoutDetail extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            workout: null,
            personalRecords: [],
            sharedBy: null,
            isLoading: true,
            notFound: false,
        };
    }

    componentDidMount() {
        this.fetchSharedWorkout();
    }

    getNumberOfExercisesDone = (workout) => {
        let total = 0;
        workout.routines?.forEach(routine => {
            total += routine.exercises.length;
        });
        return total;
    }

    fetchSharedWorkout = async () => {
        try {
            const response = await fetch(UrlBuilder.workout.sharedWorkoutApi(this.props.params.token));
            const result = await response.json();

            if (!result.data?.workout) {
                this.setState({isLoading: false, notFound: true});
                return;
            }

            this.setState({
                workout: result.data.workout,
                personalRecords: result.data.personalRecords || [],
                sharedBy: result.data.sharedBy,
                isLoading: false,
            });
        } catch (err) {
            this.setState({isLoading: false, notFound: true});
        }
    }

    renderMood = (moodLevel) => {
        switch (moodLevel) {
            case 1:
                return <SentimentVeryDissatisfied color='secondary'/>;
            case 2:
                return <SentimentSatisfiedIcon color='secondary'/>;
            case 3:
                return <SentimentVerySatisfied color='secondary'/>
            default:
                return null;
        }
    }

    renderPage = () => {
        const {classes} = this.props;
        const workout = this.state.workout;
        const numberOfExercisesDone = this.getNumberOfExercisesDone(workout);

        const dateTimeStarted = moment(workout.dateTimeStarted);
        const dateTimeEnded = moment(workout.dateTimeEnded);
        let workoutDurationObj = moment.duration(dateTimeEnded.diff(dateTimeStarted));
        let workoutDuration = moment.utc(workoutDurationObj.asMilliseconds()).format('HH:mm');
        let timeRange = dateTimeStarted.format('LT') + " - " + dateTimeEnded.format('LT');

        return <>
            <Box paddingTop={1.5}/>

            {this.state.sharedBy && (
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">
                        {this.state.sharedBy}'s Workout
                    </Typography>
                    <span className={classes.sharedBadge}>
                        Shared Workout
                    </span>
                </Box>
            )}

            <PersonalRecordBanner
                personalRecords={this.state.personalRecords}
                animated={false}
            />

            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <WorkoutSmallNote title="Exercises" description={numberOfExercisesDone} smallDetail={"Total"}/>
                </Grid>
                <Grid item xs={4}>
                    <WorkoutSmallNote title="Volume" description={workout.totalVolume} smallDetail={"Total"}/>
                </Grid>
                <Grid item xs={4}>
                    <WorkoutSmallNote title="Duration" description={workoutDuration} smallDetail={timeRange}/>
                </Grid>
            </Grid>

            <br/>

            <Box display="flex" flexDirection="column" gap={0.5} mb={1}>
                <Typography
                    variant="body2"
                    sx={{lineHeight: 1.4, display: 'flex', alignItems: 'center', m: 0}}
                >
                    <strong>Date:</strong>&nbsp;
                    {dateTimeStarted.fromNow() + ' - ' + dateTimeStarted.calendar()}
                </Typography>

                {workout.moodLevel ? (
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2" mr={1}>
                            <strong>Mood:</strong>
                        </Typography>
                        <IconButton style={{padding: 0}} size="large">
                            {this.renderMood(workout.moodLevel)}
                        </IconButton>
                    </Box>
                ) : null}

                {workout.comment ? (
                    <Typography variant="body2">
                        <strong>Notes:</strong> {workout.comment}
                    </Typography>
                ) : null}
            </Box>

            <Box paddingTop={1.5}/>
            <Divider light/>
            <Box paddingTop={1.5}/>

            <WorkoutLogsSection
                workout={workout}
                personalRecords={this.state.personalRecords}
                sharedToken={this.props.params.token}
            />
        </>;
    }

    renderLoadingSkeletons = () => {
        const {classes} = this.props;
        return <>
            <br/>
            <Grid container spacing={1}>
                <Grid item xs={4}>
                    <Skeleton animation="pulse" variant="rectangular" className={classes.media}/>
                </Grid>
                <Grid item xs={4}>
                    <Skeleton animation="pulse" variant="rectangular" className={classes.media}/>
                </Grid>
                <Grid item xs={4}>
                    <Skeleton animation="pulse" variant="rectangular" className={classes.media}/>
                </Grid>
            </Grid>
            <br/>
            <Skeleton animation="wave" variant="text"/>
            <br/>
            <Skeleton animation="wave" variant="rectangular" className={classes.media}/>
            <br/>
            <Skeleton animation="wave" variant="rectangular" className={classes.media}/>
        </>;
    }

    render() {
        if (this.state.notFound) {
            return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
                <Typography variant="h5">
                    This shared workout is no longer available.
                </Typography>
            </Box>;
        }

        return <div>
            {this.state.isLoading
                ? this.renderLoadingSkeletons()
                : this.renderPage()
            }
        </div>;
    }
}

export default withStyles(useStyles)(withRouter(SharedWorkoutDetail));
